import type React from 'react';
import { graphql } from 'react-apollo';

import { compose } from 'recompose';

import { apolloFetchPolicy } from 'js/utils/apolloFetchPolicy';

import type { mappedMegaMenuAPIData, megaMenuAPIData } from 'bundles/megamenu/types/MenuData';
import { adaptMegamenuNostosJob } from 'bundles/nostos-external/adapters';
import { NOSTOS_EXTERNAL_TABLE_IDS } from 'bundles/nostos-external/constants';
import type {
  NostosExternalGetDataQueryType,
  NostosExternalGetDataQueryVariables,
} from 'bundles/nostos-external/queries';
import { NostosExternalGetDataQuery } from 'bundles/nostos-external/queries';

export type WithMegaMenuDataProps = { megaMenuAPIData?: mappedMegaMenuAPIData };

export const MEGAMENU_NOSTOS_KEY = 'megamenu_nostos_key';

/**
 * Transforms a flat array of menu items into a nested structure organized by section and domain.
 *
 * This utility function takes raw megamenu data and structures it into a hierarchical format
 * where items are grouped first by their section ID and then by their domain ID.
 */
export const groupBySectionAndDomain = (rows?: megaMenuAPIData[]) => {
  const acc: mappedMegaMenuAPIData = {};
  rows?.forEach((row) => {
    const { sectionid, domainid } = row;
    if (acc[sectionid]) {
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      if (acc[sectionid][domainid]?.length > 0) {
        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        acc[sectionid][domainid].push(row);
      } else {
        acc[sectionid][domainid] = [row];
      }
    } else {
      acc[sectionid] = { [domainid]: [row] };
    }
  });
  return acc;
};

/**
 * Higher-order component that injects megamenu data into the wrapped component.
 *
 * This HOC fetches megamenu data from the Nostos v3 external API using GraphQL, processes
 * it by grouping items by section and domain, and provides the structured data to the
 * wrapped component via the `megaMenuAPIData` prop.
 */
const withMegaMenuData =
  () =>
  <T extends {}>(BaseComponent: React.ComponentType<T & WithMegaMenuDataProps>): React.ComponentType<T> =>
    compose<T & WithMegaMenuDataProps, T>(
      graphql<{}, NostosExternalGetDataQueryType, NostosExternalGetDataQueryVariables, WithMegaMenuDataProps>(
        NostosExternalGetDataQuery,
        {
          options: () => ({
            variables: {
              input: { tableId: NOSTOS_EXTERNAL_TABLE_IDS.megamenu_nostos_job, keys: [MEGAMENU_NOSTOS_KEY] },
            },
            context: { clientName: 'gatewayGql' },
            ...apolloFetchPolicy({ cacheForLoggedOutOnly: true }),
            ssr: false,
          }),
          props: ({ data }) => {
            const elements = adaptMegamenuNostosJob<megaMenuAPIData>(data?.NostosExternal?.getData?.entries);
            const rows = elements?.[0]?.content?.load_menu;
            // current data does not have grouping by section and domain
            return {
              megaMenuAPIData: rows && groupBySectionAndDomain(rows),
            };
          },
        }
      )
    )(BaseComponent);

export default withMegaMenuData;
